import { observer } from 'mobx-react';
import { useState, useRef } from 'react';
import { Maybe, fromEmpty, fromNullable, just, nothing } from 'maybeasy';
import { T } from '../../../../../../../../Translations';
import Loader from '../../../../../../../../components/Loader';
import { DetailsState } from '../../../../../../../LearningCollectionDetailsContext';
import {
  AllExperiencesResource,
  ClientExperienceResource,
  ProductCollectionResource,
} from '../../../Types';
import { addDescriptionToExperiences } from './BuildFullExperience';
import { useFilterStoreContext } from '../../../Filters/FilterStores';
import { applyFilters } from '../../../Filters/FilteringLogic';
import { sortExperiences } from '../../../Filters/SortingLogic';
import { sortingOptions } from '../../../../Builder/ExperienceCatalog/SortTemplate/Types';
import { emptyFragment } from '../../../../../../../../EmptyFragment';
import CourseListPanel from './CourseListPanel';
import CourseDetailPanel from './CourseDetailPanel';
import FilterDrawer from './FilterDrawer';
import CollectionTray from './CollectionTray';
import { useLearningCollection } from '../../../LearningCollectionContext';
import {
  handleAddExperience,
  handleAddExperiences,
  handleRemoveExperience,
  handleRemoveExperiences,
} from '../../../Experience/AddRemoveProgram';

interface Props {
  resource: AllExperiencesResource;
  productCollection: ProductCollectionResource;
  details: DetailsState;
  index: number;
  onClose: () => void;
}

// A plain function, not a hook: it runs only once details are ready, and a hook there would
// change the hook count when details go from loading to ready (React error #310).
const searchExperiences = (
  experiences: ClientExperienceResource[],
  searchQuery: string,
): ClientExperienceResource[] => {
  if (!searchQuery) return experiences;

  const query = searchQuery.toLowerCase();
  return experiences.filter((exp) => {
    // Search in title
    const title = exp.payload.title.toLowerCase();

    // Search in school/partner name
    const schoolName = exp.payload.school_partner_name?.toLowerCase() || '';

    // Search in offering type
    const offeringType = exp.payload.offering_type?.toLowerCase() || '';

    // Search in description
    const description = exp.payload.description.getOrElseValue('').toLowerCase();

    // Search in competencies
    const competencies = (exp.payload.competency_names || []).map((c) => c.toLowerCase());
    const matchesCompetency = competencies.some((c) => c.includes(query));

    // Search in languages
    const languages = (exp.payload.supported_languages || []).map((l) => l.toLowerCase());
    const matchesLanguage = languages.some((l) => l.includes(query));

    return (
      title.includes(query) ||
      schoolName.includes(query) ||
      offeringType.includes(query) ||
      description.includes(query) ||
      matchesCompetency ||
      matchesLanguage
    );
  });
};

const EnhancedModalBody = observer(
  ({ details, resource, productCollection, index, onClose }: Props) => {
    const experiences = resource.payload.experiences;
    const { resourceState, setResourceState } = useLearningCollection();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCourseId, setSelectedCourseId] = useState<Maybe<number>>(nothing());
    const [showFilters, setShowFilters] = useState(false);
    const courseRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    // Hooks stay above the switch so every render calls the same ones (React error #310)
    const filterStores = useFilterStoreContext();

    // Direct add/remove handlers (real-time updates)
    const handleToggleCourse = (courseId: number) => {
      const isCurrentlyAdded = productCollection.payload.experiences.includes(courseId);

      if (isCurrentlyAdded) {
        handleRemoveExperience(courseId, resourceState, setResourceState, index);
      } else if (details.kind === 'ready') {
        const fullExperiences = addDescriptionToExperiences(experiences, details.data.resource);
        const course = fullExperiences.find((exp) => exp.payload.id === courseId);
        if (course) {
          handleAddExperience(course, resourceState, setResourceState, index);
        }
      }

      // Auto-scroll to the course in the list (especially useful when adding from detail view)
      const element = courseRefs.current[courseId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    const handleRemoveFromTray = (courseId: number) => {
      handleRemoveExperience(courseId, resourceState, setResourceState, index);
    };

    const handleQuickAdd = (courseIds: number[]) => {
      handleAddExperiences(courseIds, resourceState, setResourceState, index);
    };

    const handleQuickRemove = (courseIds: number[]) => {
      handleRemoveExperiences(courseIds, resourceState, setResourceState, index);
    };

    switch (details.kind) {
      case 'errored':
        return <T kind="Something went wrong" />;
      case 'loading':
        return <Loader className="h-full" />;
      case 'ready':
        return (
          <>
            {filterStores
              .map(
                ({
                  productTypeFilterStore,
                  schoolPartnerFilterStore,
                  languageFilterStore,
                  durationFilterStore,
                  licenseFilterStore,
                  competencyFilterStore,
                  sortStore,
                  productTypeOptions,
                  schoolPartnerOptions,
                  languageOptions,
                  durationOptions,
                  licenseOptions,
                  competencyOptions,
                  unlicensedToggleStore,
                  showUnlicensedToggle,
                }) => {
                  // Enrich all experiences with descriptions first so we can both filter
                  // the catalog list and pick added courses for the tray from the same source.
                  // The tray must not be affected by catalog filters (it shows everything
                  // currently in the section regardless of filter state).
                  const allEnrichedExperiences = addDescriptionToExperiences(
                    experiences,
                    details.data.resource,
                  );

                  // Apply filters to the enriched list for the catalog view
                  const enrichedFilteredExperiences = applyFilters(
                    allEnrichedExperiences,
                    productTypeFilterStore,
                    schoolPartnerFilterStore,
                    languageFilterStore,
                    durationFilterStore,
                    licenseFilterStore,
                    competencyFilterStore,
                    unlicensedToggleStore,
                  );

                  // Apply search filter (client-side) - searches across all metadata
                  const searchFilteredExperiences = searchExperiences(
                    enrichedFilteredExperiences,
                    searchQuery,
                  );

                  // Apply sorting to filtered + enriched experiences
                  const fullExperiences = sortExperiences(
                    sortStore.state.sortOption,
                    searchFilteredExperiences,
                  );

                  // Auto-select first course if none selected
                  const effectiveSelectedId: Maybe<number> = selectedCourseId.orElse(() =>
                    fromEmpty(fullExperiences).map((exps) => exps[0].payload.id),
                  );

                  // Get selected course details
                  const selectedCourse: Maybe<ClientExperienceResource> =
                    effectiveSelectedId.andThen((id) =>
                      fromNullable(fullExperiences.find((exp) => exp.payload.id === id)),
                    );

                  // Count active filters
                  const activeFiltersCount = [
                    productTypeFilterStore.state.filter.options.length > 0,
                    schoolPartnerFilterStore.state.filter.options.length > 0,
                    languageFilterStore.state.filter.options.length > 0,
                    durationFilterStore.state.filter.options.length > 0,
                    licenseFilterStore.state.filter.options.length > 0,
                    competencyFilterStore.state.filter.options.length > 0,
                    unlicensedToggleStore.showUnlicensed,
                  ].filter(Boolean).length;

                  // Scroll to selected course
                  const scrollToCourseId = (courseId: number) => {
                    const element = courseRefs.current[courseId];
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  };

                  const handleSelectCourse = (courseId: number) => {
                    setSelectedCourseId(just(courseId));
                    scrollToCourseId(courseId);
                  };

                  // Get added courses for the tray (from full collection — NOT filtered).
                  // The tray reflects what's actually in the section regardless of catalog filters.
                  const addedCourses = allEnrichedExperiences.filter((exp) =>
                    productCollection.payload.experiences.includes(exp.payload.id),
                  );

                  // Full layout with search and filters
                  return (
                    <div className="relative flex flex-col h-full">
                      {/* Main content area with split panels */}
                      <div
                        className="flex flex-1 overflow-hidden"
                        data-intercom-target="builder-v2-course-catalog-body"
                      >
                        {/* LEFT PANEL - Course List with Search/Filters */}
                        <CourseListPanel
                          experiences={fullExperiences}
                          selectedCourseId={effectiveSelectedId}
                          onSelectCourse={handleSelectCourse}
                          productCollection={productCollection}
                          index={index}
                          courseRefs={courseRefs}
                          searchQuery={searchQuery}
                          onSearchChange={setSearchQuery}
                          onFiltersClick={() => setShowFilters(true)}
                          activeFiltersCount={activeFiltersCount}
                          currentSortValue={sortStore.state.sortOption.value}
                          onSortChange={(value) => {
                            const selectedOption = sortingOptions.find(
                              (opt) => opt.value === value,
                            );
                            if (selectedOption) {
                              sortStore.updateSelectedValue(selectedOption);
                            }
                          }}
                          addedCourseIds={productCollection.payload.experiences}
                          onToggleCourse={handleToggleCourse}
                          onAddCourses={handleQuickAdd}
                          onRemoveCourses={handleQuickRemove}
                        />

                        {/* RIGHT PANEL - Course Details */}
                        <CourseDetailPanel
                          course={selectedCourse}
                          productCollection={productCollection}
                          index={index}
                          sectionName={productCollection.payload.name}
                          addedCourseIds={productCollection.payload.experiences}
                          onToggleCourse={handleToggleCourse}
                        />
                      </div>

                      {/* Collection Tray at bottom */}
                      <CollectionTray
                        addedCourses={addedCourses}
                        onRemoveCourse={handleRemoveFromTray}
                        sectionName={productCollection.payload.name}
                      />

                      {/* Filter Drawer */}
                      {showFilters && (
                        <FilterDrawer
                          onClose={() => setShowFilters(false)}
                          productTypeFilterStore={productTypeFilterStore}
                          schoolPartnerFilterStore={schoolPartnerFilterStore}
                          languageFilterStore={languageFilterStore}
                          durationFilterStore={durationFilterStore}
                          licenseFilterStore={licenseFilterStore}
                          competencyFilterStore={competencyFilterStore}
                          productTypeOptions={productTypeOptions}
                          schoolPartnerOptions={schoolPartnerOptions}
                          languageOptions={languageOptions}
                          durationOptions={durationOptions}
                          licenseOptions={licenseOptions}
                          competencyOptions={competencyOptions}
                          unlicensedToggleStore={unlicensedToggleStore}
                          showUnlicensedToggle={showUnlicensedToggle}
                          activeFiltersCount={activeFiltersCount}
                        />
                      )}
                    </div>
                  );
                },
              )
              .getOrElse(emptyFragment)}
          </>
        );
    }
  },
);

export default EnhancedModalBody;
